import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import FormActionBar from '../../../../shared/components/FormActionBar';
import { StepHeader, StepBox } from '../../../../shared/components/StepHeader';
import FieldMappingInput from '../targets/FieldMappingInput';
import DatasetEventsStep from './DatasetEventsStep';
import DatasetColumnsEditor from './DatasetColumnsEditor';
import DatasetKeysSection from './DatasetKeysSection';
import DatasetPreview from './DatasetPreview';
import { useDatasetWriteAccess, useSaveEventDataset } from '../../hooks/useEventDatasets';
import { useDatasetRetentionStart } from '../../hooks/useDatasetRetentionStart';
import {
  buildDatasetForm,
  columnFromSamplePath,
  datasetFormReducer,
  keyFieldWarnings,
  keySuggestions,
  nameProblem,
  requiredColumnNames,
  rowPathSuggestions,
  rowSample,
  validatePreviewForm,
  toEventDataset,
  toPreviewDraft,
  validateDatasetForm,
} from '../../utils/datasetFormUtils';
import { useDatasetInputTabs } from '../../hooks/useDatasetInputTabs';
import { inputErrors, inputLabel, inputsWithErrors, isDatasetPath, problemList } from '../../utils/datasetMappingUtils';
import type { DatasetFormState, EditorMode } from '../../utils/datasetFormUtils';
import type { EventDataset, EventDatasetRecord } from '../../../../types/backend';

interface DatasetEditorProps {
  customerId: string;
  mode: EditorMode;
  /** The saved definition on edit; on create, null or a definition to start from. */
  initial: EventDataset | null;
  /** A sample event passed in; the operator can paste one instead. */
  sampleEvent?: Record<string, unknown>;
  /** The event pattern a new dataset starts from when no definition is given. */
  startPattern?: Record<string, unknown>;
  /** The definition it starts from came from a dataset template. */
  fromTemplate?: boolean;
  onSaved: (record: EventDatasetRecord) => void;
  /** Tells the operator a save was refused; the refusal also shows above the form. */
  onSaveFailed?: (message: string) => void;
  /** Opens a create editor holding this definition, its name emptied. */
  onCreateInstead: (definition: EventDataset) => void;
  onDiscard: () => void;
}

const acceptsRowPath = (path: string) => isDatasetPath(path, false);

const DatasetEditor: React.FC<DatasetEditorProps> = ({
  customerId,
  mode,
  initial,
  sampleEvent,
  startPattern,
  fromTemplate = false,
  onSaved,
  onSaveFailed,
  onCreateInstead,
  onDiscard,
}) => {
  const [form, dispatch] = useReducer(datasetFormReducer, null, () => buildDatasetForm(initial, mode, startPattern));
  const [sample, setSample] = useState<Record<string, unknown>>(sampleEvent ?? {});
  // The input whose pattern and sources the form shows.
  const tabs = useDatasetInputTabs(form, dispatch);
  const { selected, input } = tabs;
  // Errors show at their fields only once a save or a preview was tried (FE-07); a preview leaves the name out.
  const [attempted, setAttempted] = useState<'none' | 'preview' | 'save'>('none');
  const [problem, setProblem] = useState<string | null>(null);
  const { save, saving, refusal } = useSaveEventDataset(customerId);
  // An escalation that lapses while the editor is open keeps the form and its preview but takes the save away.
  const allowed = useDatasetWriteAccess();
  const alertsRef = useRef<HTMLDivElement>(null);
  // A refused or blocked save is shown above the form, which may be scrolled away from the save button.
  const refusalMessage = refusal?.message;
  useEffect(() => {
    if (problem || refusalMessage) alertsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [problem, refusalMessage]);

  const editing = mode === 'edit';
  const retentionHelper = useDatasetRetentionStart(customerId, editing, form.retentionDays, dispatch);
  const fromEvent = !editing && startPattern !== undefined;
  let errors: Record<string, string> = {};
  if (attempted === 'save') errors = validateDatasetForm(form);
  else if (attempted === 'preview') errors = validatePreviewForm(form);
  const setField = <K extends keyof DatasetFormState>(field: K, value: DatasetFormState[K]) =>
    dispatch({ type: 'SET_FIELD', field, value });

  const shownErrors = inputErrors(errors, selected);
  const withPattern = (problems: string[]) => [...tabs.patternProblems(), ...problems];
  const formProblems = () => withPattern(problemList(validateDatasetForm(form), form.inputs.length));
  const row = rowSample(sample, form.rowPath);
  const rowPathOffers = editing || form.rowPath ? [] : rowPathSuggestions(sample);

  const handleSave = () => {
    setAttempted('save');
    const problems = formProblems();
    if (problems.length > 0) {
      setProblem(`Fix before saving: ${problems.join('; ')}`);
      return;
    }
    setProblem(null);
    save(toEventDataset(form, customerId), editing, {
      onDone: onSaved,
      onFailed: (message) => onSaveFailed?.(message),
    });
  };

  const createInstead = () => onCreateInstead({ ...toEventDataset(form, customerId), name: '' });

  return (
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }} data-testid="dataset-editor">
      {/* Form column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {editing ? 'Edit Dataset' : 'Create Dataset'}
          </Typography>
          {fromEvent && <Chip label="From Event" size="small" color="primary" variant="outlined" />}
          {!editing && fromTemplate && <Chip label="From Template" size="small" color="secondary" variant="outlined" />}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }} data-testid="dataset-editor-body">
          <Box sx={{ maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 3 }} data-testid="dataset-editor-content">
            <Box ref={alertsRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1, '&:empty': { display: 'none' } }}>
            {problem && <InlineAlert severity="error" onClose={() => setProblem(null)}>{problem}</InlineAlert>}
            {refusal && (
              <InlineAlert
                severity="error"
                action={refusal.breaking ? (
                  <Button color="inherit" size="small" onClick={createInstead}>Create a new dataset instead</Button>
                ) : undefined}
              >
                {refusal.message}
              </InlineAlert>
            )}
            </Box>
            {editing && (
              <InlineAlert severity="info">
                A saved dataset takes only additive changes. The saved inputs, row path, key fields, ordering, current
                state and the existing columns are read-only here; changing them needs a new dataset. You can add an
                input, add optional columns, add values or bands to a translation and change the retention.
              </InlineAlert>
            )}

            <TextField
              required
              size="small"
              label="Name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              disabled={editing}
              error={!!(nameProblem(form.name) || errors.name)}
              helperText={nameProblem(form.name) || errors.name || 'Lowercase letters, digits and underscore'}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
              sx={{ maxWidth: 400 }}
            />

            <DatasetEventsStep
              customerId={customerId}
              inputs={form.inputs}
              tabs={tabs}
              editing={editing}
              sample={sample}
              errors={shownErrors}
              withErrors={inputsWithErrors(errors)}
              disabled={saving}
              onPatternChange={(n, pattern) => dispatch({ type: 'SET_INPUT_PATTERN', input: n, pattern })}
              onSampleChange={setSample}
            />

            <Box>
              <StepHeader
                number={2}
                title="One row is"
                helper="Where in each event the rows are; empty keeps one row per event. Example: detail.findings gives one row per finding."
              />
              <StepBox>
                <Box sx={{ maxWidth: 480 }}>
                  <FieldMappingInput
                    label="Row path"
                    value={form.rowPath}
                    onChange={(path) => setField('rowPath', path)}
                    sampleEvent={sample}
                    disabled={editing}
                    emptyText="Empty means one event is one row"
                    error={errors.rowPath}
                    pathFilter={acceptsRowPath}
                  />
                </Box>
                {rowPathOffers.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {rowPathOffers.map((o) => (
                      <Button key={o.path} size="small" variant="outlined" onClick={() => setField('rowPath', o.path)}>
                        {o.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </StepBox>
            </Box>

            <Box>
              <StepHeader
                number={3}
                title="Columns"
                helper="The values each row keeps, besides the standard ones. Example: severity read from Severity.Label."
              />
              <StepBox>
                {form.inputs.length > 1 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Where each column reads from in {inputLabel(selected)}. Pick another input in step 1.
                  </Typography>
                )}
                <DatasetColumnsEditor
                  mode={mode}
                  columns={form.columns}
                  input={selected}
                  inputLocked={editing && input.locked}
                  keyFields={form.keyFields}
                  orderingField={form.orderingField}
                  hasRowPath={!!form.rowPath.trim()}
                  resourcePath={input.resourcePath}
                  resourceRequired={form.resourceRequired}
                  rowSample={row}
                  errors={shownErrors}
                  disabled={saving}
                  onAdd={() => dispatch({ type: 'ADD_COLUMN', required: false })}
                  onUpdate={(index, changes) => dispatch({ type: 'UPDATE_COLUMN', index, changes })}
                  onSourceChange={(column, source) => dispatch({ type: 'SET_SOURCE', column, input: selected, source })}
                  onRemove={(index) => dispatch({ type: 'REMOVE_COLUMN', index })}
                  onResourcePathChange={(path) => dispatch({ type: 'SET_INPUT_RESOURCE', input: selected, path })}
                  onResourceRequiredChange={(required) => setField('resourceRequired', required)}
                  onPickSampleField={(path, value) =>
                    dispatch({ type: 'ADD_SAMPLE_COLUMN', column: columnFromSamplePath(path, value, form, selected) })}
                />
              </StepBox>
            </Box>

            <Box>
              <StepHeader
                number={4}
                title="Identity and history"
                helper="What a row is about, so its history can be followed. Example: finding_id keeps one current row per finding."
              />
              <StepBox>
                <DatasetKeysSection
                  requiredColumns={requiredColumnNames(form)}
                  optionalColumns={form.columns.filter((c) => !c.required && c.name).map((c) => c.name)}
                  keySuggestions={keySuggestions(form)}
                  onUseAsKey={(index) => dispatch({ type: 'USE_AS_KEY', index })}
                  keyFields={form.keyFields}
                  keyWarnings={keyFieldWarnings(form)}
                  orderingField={form.orderingField}
                  keepCurrentState={form.keepCurrentState}
                  locked={editing}
                  errors={errors}
                  onToggleKey={(column) => dispatch({ type: 'TOGGLE_KEY', column })}
                  onOrderingChange={(column) => setField('orderingField', column)}
                  onKeepCurrentStateChange={(keep) => setField('keepCurrentState', keep)}
                />
              </StepBox>
            </Box>

            <Box>
              <StepHeader
                number={5}
                title="Retention"
                helper="How many days of rows to keep, at most the raw archive retention. Example: 90."
              />
              <StepBox>
                <TextField
                  required
                  size="small"
                  type="number"
                  label="Retention (days)"
                  value={form.retentionDays}
                  onChange={(e) => setField('retentionDays', e.target.value)}
                  error={!!errors.retentionDays}
                  helperText={errors.retentionDays || retentionHelper}
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 200 }}
                />
              </StepBox>
            </Box>
          </Box>
        </Box>

        <FormActionBar
          onSave={handleSave}
          saving={saving}
          saveDisabled={saving}
          hideSave={!allowed}
          saveLabel={editing ? 'Save Changes' : 'Create Dataset'}
          size="small"
          sx={{ px: 3, py: 1.5, mt: 0, pt: 0 }}
        >
          <Button variant="outlined" size="small" onClick={onDiscard} disabled={saving}>Discard</Button>
        </FormActionBar>
      </Box>

      {/* Preview rail */}
      <DatasetPreview
        customerId={customerId}
        buildDraft={() => toPreviewDraft(form, customerId)}
        sample={sample}
        check={() => {
          if (attempted !== 'save') setAttempted('preview');
          return withPattern(problemList(validatePreviewForm(form), form.inputs.length));
        }}
      />
    </Box>
  );
};

export default DatasetEditor;
